import { useState } from 'react';
import { useCreateEntity, useUpdateEntity } from '@/hooks/useReference';
import { useBillingNouns } from '@/hooks/useSession';
import type { BillableParty } from '@/types';
import { Sheet } from '@m/ui/Sheet';
import { BarButton } from '@m/ui/Controls';
import { Group } from '@m/ui/List';
import { FieldRow } from './forms';

/** Add or edit a billing party: who the invoice is addressed and emailed to. */
export function BillingPartySheet({
  open,
  onClose,
  party,
  initialName = '',
  onCreated
}: {
  open: boolean;
  onClose: () => void;
  party: BillableParty | null;
  initialName?: string;
  onCreated?: (p: BillableParty) => void;
}) {
  const nouns = useBillingNouns();
  const create = useCreateEntity('billable');
  const update = useUpdateEntity('billable');
  const initial = {
    name: party?.name ?? initialName,
    organization: party?.organization ?? '',
    title: party?.title ?? '',
    emails: (party?.emails ?? []).join(', '),
    phone: party?.phone ?? '',
    address: party?.address ?? ''
  };
  const [f, setF] = useState(initial);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const dirty = JSON.stringify(f) !== JSON.stringify(initial);

  const save = async () => {
    if (!f.name.trim()) return;
    const body = {
      name: f.name.trim(),
      organization: f.organization.trim() || null,
      title: f.title.trim() || null,
      emails: f.emails.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
      phone: f.phone.trim() || null,
      address: f.address.trim() || null
    };
    if (party) update.mutate({ id: party.id, patch: body });
    else {
      const created = (await create.mutateAsync(body)) as BillableParty | undefined;
      if (created) onCreated?.(created);
    }
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      full
      dirty={dirty}
      title={party ? 'Edit' : `New ${nouns.payer.toLowerCase()}`}
      leading={<BarButton onClick={onClose}>Cancel</BarButton>}
      trailing={
        <BarButton className="font-semibold" disabled={!f.name.trim() || !dirty || create.isPending} onClick={() => void save()}>
          Save
        </BarButton>
      }
    >
      <div className="flex flex-col gap-6">
        <Group>
          <FieldRow label="Name" value={f.name} onChange={set('name')} placeholder="Required" autoFocus={!party} />
          <FieldRow label="Organization" value={f.organization} onChange={set('organization')} placeholder="Optional" />
          <FieldRow label="Title" value={f.title} onChange={set('title')} placeholder="Optional" />
        </Group>
        <Group header="Invoice email" footer="Separate several addresses with commas.">
          <FieldRow label="Email" type="email" inputMode="email" autoCapitalize="off" value={f.emails} onChange={set('emails')} placeholder="billing@example.com" />
        </Group>
        <Group header="Contact">
          <FieldRow label="Phone" type="tel" value={f.phone} onChange={set('phone')} placeholder="Optional" />
          <FieldRow label="Address" value={f.address} onChange={set('address')} placeholder="Optional" />
        </Group>
      </div>
    </Sheet>
  );
}
